import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Activity, User, Mail, Lock, Eye, EyeOff, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/UI/LoadingSpinner';

const Register = () => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [usernameError, setUsernameError] = useState('');

  const { register } = useAuth();
  const navigate = useNavigate();

  const validateUsername = (username) => {
    if (username.length === 0) {
      setUsernameError('');
      return;
    }

    if (username.length < 3) {
      setUsernameError('Username must be at least 3 characters');
      return;
    }

    if (username.length > 50) {
      setUsernameError('Username must be less than 50 characters');
      return;
    }

    const scriptPattern = /<script|javascript:|onerror=|onload=|<iframe|eval\(|alert\(/i;
    if (scriptPattern.test(username)) {
      setUsernameError('Username contains invalid content');
      return;
    }

    setUsernameError('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (name === 'username') {
      validateUsername(value);
    }
  };

  const validateForm = () => {
    if (usernameError) {
      toast.error(usernameError);
      return false;
    }

    if (formData.username.length < 3) {
      toast.error('Username must be at least 3 characters long');
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return false;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);

    try {
      const result = await register(formData.username, formData.email, formData.password);
      if (result.success) {
        toast.success(result.message || 'Please check your email to verify your account.');
        navigate('/login');
      } else {
        toast.error(result.error);
        if (result.details && result.details.length > 1) {
          result.details.slice(1).forEach(detail => {
            setTimeout(() => {
              toast.error(detail.msg || detail.message);
            }, 100);
          });
        }
      }
    } catch (error) {
      toast.error('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-50 dark:bg-surface-950 px-4 sm:px-6 lg:px-8 mesh-bg py-20">
      <div className="max-w-md w-full animate-slide-up">
        <div className="card-premium p-8 lg:p-10">
          <div className="text-center mb-10">
            <div className="flex justify-center mb-6">
              <Link to="/" className="p-3 bg-brand-600 rounded-2xl shadow-glow-brand animate-float hover:scale-110 transition-transform">
                <Activity className="h-10 w-10 text-white" />
              </Link>
            </div>
            <h2 className="text-3xl font-bold text-surface-900 dark:text-white mb-2">
              Create account
            </h2>
            <p className="text-surface-600 dark:text-surface-400">
              Start monitoring your infrastructure today
            </p>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-surface-700 dark:text-surface-300 mb-2">
                  Username
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-surface-400 group-focus-within:text-brand-500 transition-colors" />
                  </div>
                  <input
                    name="username"
                    type="text"
                    required
                    className={`input-premium pl-12 ${usernameError ? 'border-danger focus:ring-danger/20 focus:border-danger' : ''}`}
                    placeholder="johndoe"
                    value={formData.username}
                    onChange={handleChange}
                  />
                </div>
                {usernameError && (
                  <p className="mt-2 text-xs text-danger font-medium">{usernameError}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-surface-700 dark:text-surface-300 mb-2">
                  Email Address
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-surface-400 group-focus-within:text-brand-500 transition-colors" />
                  </div>
                  <input
                    name="email"
                    type="email"
                    required
                    className="input-premium pl-12"
                    placeholder="name@company.com"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-surface-700 dark:text-surface-300 mb-2">
                  Password
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-surface-400 group-focus-within:text-brand-500 transition-colors" />
                  </div>
                  <input
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    className="input-premium pl-12 pr-12"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-4 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-surface-400 hover:text-surface-600" />
                    ) : (
                      <Eye className="h-5 w-5 text-surface-400 hover:text-surface-600" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-surface-700 dark:text-surface-300 mb-2">
                  Confirm Password
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-surface-400 group-focus-within:text-brand-500 transition-colors" />
                  </div>
                  <input
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    required
                    className="input-premium pl-12 pr-12"
                    placeholder="••••••••"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-4 flex items-center"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-5 w-5 text-surface-400 hover:text-surface-600" />
                    ) : (
                      <Eye className="h-5 w-5 text-surface-400 hover:text-surface-600" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary py-3.5 text-base flex items-center justify-center font-bold"
            >
              {loading ? (
                <LoadingSpinner size="sm" />
              ) : (
                <>
                  Create Account <ArrowRight className="ml-2 h-5 w-5" />
                </>
              )}
            </button>

            <div className="text-center pt-2">
              <p className="text-sm text-surface-600 dark:text-surface-400">
                Already have an account?{' '}
                <Link
                  to="/login"
                  className="font-bold text-brand-600 dark:text-brand-400 hover:underline"
                >
                  Sign In
                </Link>
              </p>
            </div>
          </form>
        </div>

        <p className="mt-8 text-center text-xs text-surface-500 tracking-widest uppercase">
          &copy; {new Date().getFullYear()} UptimeMonitor Infrastructure
        </p>
      </div>
    </div>
  );
};

export default Register;
